import { supabase } from '../lib/supabase';

// Service for managing song analyses with Supabase
export class SongAnalysisService {
  
  /**
   * Create a new song analysis
   * @param {Object} analysisData - The complete song analysis data
   * @returns {Promise<{data: Object|null, error: Object|null}>}
   */
  static async createSongAnalysis(analysisData) {
    if (!analysisData) {
      return { error: { message: 'Song data is required' } };
    }

    try {
      const { data, error } = await supabase?.from('song_analyses')?.insert([analysisData])?.select()?.single();

      return { data, error };
    } catch (err) {
      return { error: { message: 'Failed to create song analysis' } };
    }
  }

  /**
   * Get all song analyses with optional search
   * @param {string} searchTerm - Optional search term for filtering
   * @returns {Promise<{data: Array|null, error: Object|null}>}
   */
  static async getAllSongs(searchTerm = '') {
    try {
      if (searchTerm?.trim()) {
        // Use the database search function for better performance
        const { data, error } = await supabase?.rpc('search_song_analyses', { search_text: searchTerm?.trim() });

        if (error) {
          return { data: null, error };
        }

        return { data: data || [], error: null };
      } else {
        // Get all analyses ordered by creation date with user profile information
        const { data, error } = await supabase?.from('song_analyses')?.select(`
            *,
            created_by_profile:user_profiles!song_analyses_created_by_fkey(full_name, username, email),
            updated_by_profile:user_profiles!song_analyses_updated_by_fkey(full_name, username, email)
          `)?.order('created_at', { ascending: false });

        if (error) {
          return { data: null, error };
        }

        return { data: data || [], error: null };
      }
    } catch (err) {
      return { data: null, error: { message: err?.message || 'Unknown error occurred' } };
    }
  }

  /**
   * Get all song analyses with optional search (alias for compatibility)
   * @param {string} searchTerm - Optional search term for filtering
   * @returns {Promise<{data: Array|null, error: Object|null}>}
   */
  static async getSongAnalyses(searchTerm = '') {
    return this.getAllSongs(searchTerm);
  }

  /**
   * Get a single song analysis by ID
   * @param {string} id - The UUID of the song analysis
   * @returns {Promise<{data: Object|null, error: Object|null}>}
   */
  static async getSongAnalysisById(id) {
    try {
      const { data, error } = await supabase?.from('song_analyses')?.select(`
          *,
          created_by_profile:user_profiles!song_analyses_created_by_fkey(full_name, username, email),
          updated_by_profile:user_profiles!song_analyses_updated_by_fkey(full_name, username, email)
        `)?.eq('id', id);

      if (error) {
        return { data: null, error };
      }

      // Check if we got results
      if (!data || data?.length === 0) {
        return { data: null, error: { message: 'Song not found' } };
      }

      // If multiple results (shouldn't happen with UUID), take the first one
      const songData = data?.length > 1 ? data?.[0] : data?.[0];

      // Transform to frontend format
      const transformedData = this.transformToFrontendFormat(songData);

      return { data: transformedData, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message || 'Unknown error occurred' } };
    }
  }

  /**
   * Update an existing song analysis
   * @param {string} id - The UUID of the song analysis to update
   * @param {Object} updateData - The data to update
   * @returns {Promise<{data: Object|null, error: Object|null}>}
   */
  static async updateSongAnalysis(id, updateData) {
    if (!id || !updateData) {
      return { error: { message: 'ID and song data are required' } };
    }

    try {
      const { data, error } = await supabase?.from('song_analyses')?.update({
          overview: updateData?.overview,
          level1: updateData?.level1,
          level2: updateData?.level2,
          level3: updateData?.level3,
          level4: updateData?.level4,
          level5: updateData?.level5,
          phrases: updateData?.phrases
        })?.eq('id', id)?.select()?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message || 'Unknown error occurred' } };
    }
  }

  /**
   * Delete a song analysis
   * @param {string} id - The UUID of the song analysis to delete
   * @returns {Promise<{error: Object|null}>}
   */
  static async deleteSongAnalysis(id) {
    try {
      const { error } = await supabase?.from('song_analyses')?.delete()?.eq('id', id);

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (err) {
      return { error: { message: err?.message || 'Unknown error occurred' } };
    }
  }

  /**
   * Get analytics/stats about song analyses
   * @returns {Promise<{data: Object|null, error: Object|null}>}
   */
  static async getAnalyticsStats() {
    try {
      // Remove team_member from select query
      const { data, error } = await supabase?.from('song_analyses')?.select('overview, created_at');

      if (error) {
        return { data: null, error };
      }

      // Process stats on client side - remove team member analytics
      const stats = {
        total: data?.length || 0,
        byMusical: {},
        recentCount: 0
      };

      const oneWeekAgo = new Date();
      oneWeekAgo?.setDate(oneWeekAgo?.getDate() - 7);

      data?.forEach(analysis => {
        // Count by musical
        const musical = analysis?.overview?.musical;
        if (musical) {
          stats.byMusical[musical] = (stats?.byMusical?.[musical] || 0) + 1;
        }

        // Count recent analyses
        if (new Date(analysis?.created_at) > oneWeekAgo) {
          stats.recentCount++;
        }
      });

      return { data: stats, error: null };
    } catch (err) {
      return { data: null, error: { message: err?.message || 'Unknown error occurred' } };
    }
  }

  /**
   * Transform database row to frontend format
   * @param {Object} dbRow - Raw database row
   * @returns {Object} - Formatted data for frontend
   */
  static transformToFrontendFormat(dbRow) {
    if (!dbRow) return null;

    try {
      // Parse JSON fields safely
      const overview = typeof dbRow?.overview === 'string' ? JSON?.parse(dbRow?.overview ||'{}') : 
        (dbRow?.overview || {});

      const level3 = typeof dbRow?.level3 === 'string' ? JSON?.parse(dbRow?.level3 ||'[]') : 
        (dbRow?.level3 || []);

      const level4 = typeof dbRow?.level4 === 'string' ? JSON?.parse(dbRow?.level4 ||'{}') : 
        (dbRow?.level4 || {});

      const level5 = typeof dbRow?.level5 === 'string' ? JSON?.parse(dbRow?.level5 ||'{}') : 
        (dbRow?.level5 || {});

      const phrases = typeof dbRow?.phrases === 'string' ? JSON?.parse(dbRow?.phrases ||'[]') : 
        (dbRow?.phrases || []);

      // Get user names from the joined user_profiles data
      const createdByName = dbRow?.created_by_profile?.full_name || 
        dbRow?.created_by_profile?.username || 
        dbRow?.created_by_profile?.email || 
        'Unknown User';

      const updatedByName = dbRow?.updated_by_profile?.full_name || 
        dbRow?.updated_by_profile?.username || 
        dbRow?.updated_by_profile?.email || 
        null;

      // Enhanced range and style extraction from multiple sources
      const extractRange = () => {
        // Check overview first
        if (overview?.range && overview?.range?.trim()) {
          return overview?.range?.trim();
        }
        // Check level4 for range data
        if (level4?.range && level4?.range?.trim()) {
          return level4?.range?.trim();
        }
        // Check overview for vocal range patterns
        if (overview?.vocalRange && overview?.vocalRange?.trim()) {
          return overview?.vocalRange?.trim();
        }
        // Check level4 for vocal range patterns  
        if (level4?.vocalRange && level4?.vocalRange?.trim()) {
          return level4?.vocalRange?.trim();
        }
        // Check for key signature as range indicator
        if (overview?.musicalKey && overview?.musicalKey?.trim()) {
          return `Key: ${overview?.musicalKey?.trim()}`;
        }
        // Check time signature as additional vocal info
        if (overview?.timeSignature && overview?.timeSignature?.trim()) {
          return `Time: ${overview?.timeSignature?.trim()}`;
        }
        return 'Range not specified';
      };

      const extractStyle = () => {
        // Check overview first
        if (overview?.style && overview?.style?.trim()) {
          return overview?.style?.trim();
        }
        // Check level4 for style data
        if (level4?.style && level4?.style?.trim()) {
          return level4?.style?.trim();
        }
        // Check overview for type of song as style indicator
        if (overview?.typeOfSong && overview?.typeOfSong?.trim()) {
          return overview?.typeOfSong?.trim();
        }
        // Check level4 for tone as style indicator
        if (level4?.tone && level4?.tone?.trim()) {
          return level4?.tone?.trim();
        }
        // Check level4 for tempo as style indicator
        if (level4?.tempo && level4?.tempo?.trim()) {
          return `Tempo: ${level4?.tempo?.trim()}`;
        }
        // Check overview for musical key as style context
        if (overview?.musicalKey && overview?.timeSignature) {
          return `${overview?.musicalKey} - ${overview?.timeSignature}`;
        }
        return 'Style not specified';
      };

      return {
        id: dbRow?.id,
        overview: {
          musical: overview?.musical || '',
          songTitle: overview?.songTitle || overview?.song_title || '',
          character: overview?.character || '',
          momentInShow: overview?.momentInShow || overview?.moment_in_show || '',
          songPlacementInShow: overview?.songPlacementInShow || overview?.momentInShow || overview?.moment_in_show || '',
          emotionalCore: overview?.emotionalCore || overview?.emotional_core || '',
          objective: overview?.objective || '',
          typeOfSong: overview?.typeOfSong || overview?.type_of_song || '',
          timeSignature: overview?.timeSignature || overview?.time_signature || '',
          musicalKey: overview?.musicalKey || overview?.musical_key || '',
          estimatedRuntime: overview?.estimatedRuntime || overview?.estimated_runtime || '',
          range: extractRange(), // Enhanced range extraction
          style: extractStyle()  // Enhanced style extraction
        },
        // Add user information for display
        createdByName,
        updatedByName,
        levelOne: {
          tone: level4?.tone || '',
          emotionalBeat: level4?.emotionalBeat || level4?.emotional_beat || '',
          whenToUse: level4?.whenToUse || level4?.when_to_use || '',
          whenNotToUse: level4?.whenNotToUse || level4?.when_not_to_use || '',
          style: level4?.style || '',
          tempo: level4?.tempo || ''
        },
        levelTwo: {
          storyContext: dbRow?.level1 || '',
          musicalFacts: dbRow?.level2 || ''
        },
        levelThree: {
          vocalsOneComments: level3?.[0]?.comments || level3?.vocalsOneComments || '',
          vocalsThreeComments: level3?.[1]?.comments || level3?.vocalsThreeComments || '',
          vocalsFiveComments: level3?.[2]?.comments || level3?.vocalsFiveComments || ''
        },
        levelFour: {
          beginnerChallenges: level4?.beginnerChallenges || level4?.beginner_challenges || '',
          advancedChallenges: level4?.advancedChallenges || level4?.advanced_challenges || '',
          breathingNotes: level4?.breathingNotes || level4?.breathing_notes || '',
          singingActingBalance: level4?.singingActingBalance || level4?.singing_acting_balance || ''
        },
        levelFive: {
          accompaniment: level5?.accompaniment || '',
          arranging: level5?.arranging || '',
          styleChoices: level5?.styleChoices || level5?.style_choices || '',
          cuttingForAuditions: level5?.cuttingForAuditions || level5?.cutting_for_auditions || ''
        },
        phrases: {
          musicalPhrases: {
            phrase: phrases?.[0]?.phrase || phrases?.musicalPhrases?.phrase || '',
            lyric: phrases?.[0]?.lyric || phrases?.musicalPhrases?.lyric || '',
            notes: phrases?.[0]?.notes || phrases?.musicalPhrases?.notes || '',
            measures: phrases?.[0]?.measures || phrases?.musicalPhrases?.measures || ''
          },
          vocalChallenges: {
            phrase: phrases?.[1]?.phrase || phrases?.vocalChallenges?.phrase || '',
            lyric: phrases?.[1]?.lyric || phrases?.vocalChallenges?.lyric || '',
            notes: phrases?.[1]?.notes || phrases?.vocalChallenges?.notes || '',
            measures: phrases?.[1]?.measures || phrases?.vocalChallenges?.measures || ''
          }
        },
        // Raw database fields for access in components
        level1: dbRow?.level1,
        level2: dbRow?.level2,
        level3: level3,
        level4: level4,
        level5: level5,
        phrases: phrases,
        createdAt: dbRow?.created_at,
        createdBy: dbRow?.created_by,
        updatedAt: dbRow?.updated_at,
        updatedBy: dbRow?.updated_by
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Transform frontend data to database format
   * @param {Object} frontendData - Frontend form data
   * @returns {Object} - Formatted data for database
   */
  static transformToDatabaseFormat(frontendData) {
    if (!frontendData) return null;

    try {
      return {
        overview: JSON?.stringify(frontendData?.overview || {}),
        level1: frontendData?.levelTwo?.storyContext || '',
        level2: frontendData?.levelTwo?.musicalFacts || '',
        level3: JSON?.stringify([
          { level: 1, comments: frontendData?.levelThree?.vocalsOneComments || '' },
          { level: 3, comments: frontendData?.levelThree?.vocalsThreeComments || '' },
          { level: 5, comments: frontendData?.levelThree?.vocalsFiveComments || '' }
        ]),
        level4: JSON?.stringify({
          ...frontendData?.levelOne,
          ...frontendData?.levelFour
        }),
        level5: JSON?.stringify(frontendData?.levelFive || {}),
        phrases: JSON?.stringify([
          {
            type: 'musical',
            phrase: frontendData?.phrases?.musicalPhrases?.phrase || '',
            lyric: frontendData?.phrases?.musicalPhrases?.lyric || '',
            notes: frontendData?.phrases?.musicalPhrases?.notes || '',
            measures: frontendData?.phrases?.musicalPhrases?.measures || ''
          },
          {
            type: 'vocal_challenge',
            phrase: frontendData?.phrases?.vocalChallenges?.phrase || '',
            lyric: frontendData?.phrases?.vocalChallenges?.lyric || '',
            notes: frontendData?.phrases?.vocalChallenges?.notes || '',
            measures: frontendData?.phrases?.vocalChallenges?.measures || ''
          }
        ])
        // Remove team_member assignment completely
        // created_by and updated_by will be set in the form component
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Transform frontend data to backend format (alias for compatibility)
   * @param {Object} frontendData - Frontend form data
   * @returns {Object} - Formatted data for database
   */
  static transformToBackendFormat(frontendData) {
    if (!frontendData) return null;

    try {
      // Transform the new song addition form structure to database format
      return {
        overview: JSON?.stringify(frontendData?.overview || {}),
        level1: frontendData?.levelOne?.lyrics || '',
        level2: frontendData?.levelTwo?.basicDiscussion || '',
        level3: JSON?.stringify([
          {
            section: 'opening',
            referenceLyricLine: frontendData?.levelThree?.opening?.referenceLyricLine || '',
            description: frontendData?.levelThree?.opening?.description || ''
          },
          {
            section: 'middle',
            referenceLyricLine: frontendData?.levelThree?.middle?.referenceLyricLine || '',
            description: frontendData?.levelThree?.middle?.description || ''
          },
          {
            section: 'ending',
            referenceLyricLine: frontendData?.levelThree?.ending?.referenceLyricLine || '',
            description: frontendData?.levelThree?.ending?.description || ''
          }
        ]),
        level4: JSON?.stringify({
          backstory: frontendData?.levelFour?.backstory || '',
          arc: frontendData?.levelFour?.arc || '',
          physicality: frontendData?.levelFour?.physicality || '',
          facialExpressions: frontendData?.levelFour?.facialExpressions || '',
          connection: frontendData?.levelFour?.connection || '',
          exercises: {
            exercise1: frontendData?.levelFour?.exercises?.exercise1 || '',
            exercise2: frontendData?.levelFour?.exercises?.exercise2 || '',
            exercise3: frontendData?.levelFour?.exercises?.exercise3 || ''
          }
        }),
        level5: JSON?.stringify({}), // Level 5 is handled in phrases
        phrases: JSON?.stringify(frontendData?.levelFive?.phrases || [])
        // Remove team_member assignment completely
        // created_by and updated_by will be set in the form component
      };
    } catch (error) {
      return null;
    }
  }

  // Add these new methods for favorites functionality
  static async getUserFavorites(userId) {
    try {
      const { data, error } = await supabase?.from('song_favorites')?.select(`
          id,
          song_id,
          created_at,
          song_analyses!inner (
            id,
            overview,
            created_at
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async addToFavorites(userId, songId) {
    try {
      const { data, error } = await supabase?.from('song_favorites')?.insert([
          {
            user_id: userId,
            song_id: songId
          }
        ]);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async removeFromFavorites(userId, songId) {
    try {
      const { data, error } = await supabase?.from('song_favorites')?.delete()?.eq('user_id', userId)?.eq('song_id', songId);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
}

export default SongAnalysisService;